import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { AppDispatch } from '../../features/store';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentProduct, getProductsByModel, selectModelProducts } from '../../features/products';
import { getProductDetails } from '../../features/products/productThunks';
import { Product } from '../../features/products/productModels';
import Loader from '../../components/Loader';
import ProductCarousel from '../../components/ProductCarousel';
import './ProductDetail.css';
import ProductImages from './components/ProductImages';
import ProductInfo from './components/ProductInfo';
import ProductTabs from './components/ProductTabs';

const ProductDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [wishlistItems, setWishlistItems] = useState<string[]>([]);
  const dispatch: AppDispatch = useDispatch();
  const product = useSelector(selectCurrentProduct);
  const modelProducts = useSelector(selectModelProducts);
  const isLoading = useSelector((state: any) => state.products.loading);

  useEffect(() => {
    if (id) {
      dispatch(getProductDetails(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (product?.model) {
      dispatch(getProductsByModel(product.model));
    }
  }, [product?.model, dispatch]);

  // Sample product images - keeping mock images for future image array support
  const productImages = [...(product?.images || [])];

  // Helper functions to handle Product model
  const getProductPrice = (): number => {
    return typeof product?.price === 'string' ? parseFloat(product.price) : product?.price || 0;
  };
  const getProductSalePrice = (): number => {
    return typeof product?.salePrice === 'string' ? parseFloat(product.salePrice) : product?.salePrice || 0;
  };

  const getProductName = (): string => {
    return product?.productName || t('productDetail.product');
  };

  const getProductBrand = (): string => {
    return product?.brand || t('productDetail.brand');
  };

  const getProductModel = (): string => {
    return product?.model || '';
  };

  const getProductDescription = (): string => {
    return product?.description || t('productDetail.noDescriptionAvailable');
  };

  const getCategoryName = (): string => {
    if (product?.categoryName) {
      return product.categoryName;
    }
    if (product?.categoryId && typeof product.categoryId === 'object') {
      return product.categoryId.categoryName;
    }
    return t('productDetail.uncategorized');
  };

  const isProductEnabled = (): boolean => {
    return product?.enable || false;
  };

  const isProductOnSale = (): boolean => {
    return product?.sale || false;
  };

  const getSaleDescription = (): string => {
    return product?.saleDescription || '';
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const handleWishlistToggle = () => {
    setIsWishlisted(!isWishlisted);
  };

  const handleCarouselAddToCart = (product: Product) => {
    addToCart(product, 1);
  };

  const handleCarouselToggleWishlist = (productId: number) => {
    const id = productId.toString();
    setWishlistItems(prev => 
      prev.includes(id) 
        ? prev.filter(existingId => existingId !== id)
        : [...prev, id]
    );
  };

  // Filter out current product from model products
  const filteredModelProducts = modelProducts.filter(p => p._id !== product?._id);

  const handleImageSelect = (index: number) => {
    setSelectedImage(index);
  };

  const handlePreviousImage = () => {
    setSelectedImage(prev => 
      prev === 0 ? productImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImage(prev => 
      prev === productImages.length - 1 ? 0 : prev + 1
    );
  };

  if (isLoading) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <Loader size="large" text={t('productDetail.loadingProductDetails')} />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="not-found">
            <h2>{t('productDetail.productNotFound')}</h2>
            <p>{t('productDetail.productNotFoundMessage')}</p>
            <button 
              className="back-button"
              onClick={() => navigate('/')}
            >
              {t('productDetail.backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <button 
            className="back-link"
            onClick={() => navigate(-1)}
          >
            ← {t('productDetail.back')}
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item">{getCategoryName()}</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item">{getProductBrand()}</span>
        </nav>

        <div className="product-detail-content">
          {/* Product Images */}
          <ProductImages
            images={productImages}
            selectedImage={selectedImage}
            onImageSelect={handleImageSelect}
            onPreviousImage={handlePreviousImage}
            onNextImage={handleNextImage}
            productName={getProductName()}
          />

          {/* Product Info */}
          <ProductInfo
            product={product}
            quantity={quantity}
            isWishlisted={isWishlisted}
            onQuantityChange={handleQuantityChange}
            onAddToCart={handleAddToCart}
            onWishlistToggle={handleWishlistToggle}
            getProductPrice={getProductPrice}
            getProductName={getProductName}
            getProductBrand={getProductBrand}
            getProductModel={getProductModel}
            getProductDescription={getProductDescription}
            getCategoryName={getCategoryName}
            isProductEnabled={isProductEnabled}
            isProductOnSale={isProductOnSale}
            getSaleDescription={getSaleDescription}
            getProductSalePrice={getProductSalePrice}
          />
        </div>

        {/* Product Tabs */}
        <ProductTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          product={product}
          getProductName={getProductName}
          getProductBrand={getProductBrand}
          getProductModel={getProductModel}
          getCategoryName={getCategoryName}
          getProductDescription={getProductDescription}
        />

      
        {filteredModelProducts.length > 0 && (
          <ProductCarousel
            products={filteredModelProducts}
            title={t('productDetail.relatedProductsByModel', { model: getProductModel() })}
            onAddToCart={handleCarouselAddToCart}
            onToggleWishlist={handleCarouselToggleWishlist}
            wishlistItems={wishlistItems}
          />
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
